/**
 * Serbian bank accounts: 18 digits - 3 for the bank, 13 for the account, 2 control
 * digits - checked with MOD 97-10, exactly as the backend's isValidBankAccount does.
 *
 * Checked here as well as there because an account failing the check makes the NBS
 * validator reject the whole IPS QR code, while the PDF still looks perfectly fine.
 */

export const mod97 = (digits: string): number => {
  let remainder = 0;
  for (let i = 0; i < digits.length; i++) {
    remainder = (remainder * 10 + (digits.charCodeAt(i) - 48)) % 97;
  }
  return remainder;
};

export const bankAccountDigits = (value: string): string => (value ?? '').replace(/\D/g, '');

export const isValidBankAccount = (value: string): boolean => {
  const digits = bankAccountDigits(value);
  return /^\d{18}$/.test(digits) && mod97(digits) === 1;
};

/** 160-0000000123456-54 */
export const formatBankAccount = (digits: string): string =>
  /^\d{18}$/.test(digits) ? `${digits.slice(0, 3)}-${digits.slice(3, 16)}-${digits.slice(16)}` : digits;

/**
 * What the field shows once it is left.
 *
 * The short form printed on payment slips - 160-123456-54, the middle part without its
 * leading zeros - is padded to 18 digits; 18 digits are shown with their dashes. Anything
 * else is left as typed, so the error message can talk about what was typed.
 */
export const normalizeBankAccountInput = (value: string): string => {
  const trimmed = (value ?? '').trim();
  const short = /^(\d{3})\s*-\s*(\d{1,13})\s*-\s*(\d{2})$/.exec(trimmed);
  if (short) return formatBankAccount(`${short[1]}${short[2].padStart(13, '0')}${short[3]}`);
  const digits = bankAccountDigits(trimmed);
  if (/^[\d\s-]*$/.test(trimmed) && digits.length === 18) return formatBankAccount(digits);
  return trimmed;
};
